import { CategoryTypeEnum } from '@repo/common/enums';
import {
  BookingCountryService,
  BookingShop,
  SlotInterface,
} from '@repo/common/interfaces';
import { persist } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';
import { WidgetInformationsStepFormValues } from '@/components/Steps/WidgetInformationsStep/WidgetInformationsStep.tsx';

export type WidgetState = {
  _hasHydrated: boolean;
  modelId?: string;
  superModelId?: string;
  storeId?: string;
  categoryType?: CategoryTypeEnum;
  countryService?: BookingCountryService;
  shop?: BookingShop;
  timeSlot?: SlotInterface;
  informations?: WidgetInformationsStepFormValues;
  user?: {
    id?: string;
    email?: string;
  };
};

export type WidgetActions = {
  setModelId: (modelId?: string) => void;
  setSuperModelId: (superModelId?: string) => void;
  setStoreId: (storeId?: string) => void;
  setCategoryType: (category?: CategoryTypeEnum) => void;
  setCountryService: (countryService?: BookingCountryService) => void;
  setShop: (shop?: BookingShop) => void;
  setTimeSlot: (timeSlot?: SlotInterface) => void;
  setInformations: (informations?: WidgetInformationsStepFormValues) => void;
  setUser: (userInfo?: { sub: string; email: string }) => void;
  reset: () => void;
  setHasHydrated: (value: boolean) => void;
};

export type WidgetStore = WidgetState & WidgetActions;

export const defaultInitState: WidgetState = {
  _hasHydrated: false,
  modelId: undefined,
  superModelId: undefined,
  storeId: undefined,
  categoryType: undefined,
  countryService: undefined,
  shop: undefined,
  timeSlot: undefined,
  informations: undefined,
  user: undefined,
};

export const createWidgetStore = (
  initState: WidgetState = defaultInitState,
) => {
  return createStore<WidgetStore>()(
    persist(
      (set) => ({
        ...initState,
        setModelId: (modelId?: string) =>
          set((state) => ({
            ...state,
            modelId,
          })),
        setSuperModelId: (superModelId?: string) =>
          set((state) => ({
            ...state,
            superModelId,
          })),
        setStoreId: (storeId?: string) =>
          set((state) => ({
            ...state,
            storeId,
          })),
        setCategoryType: (categoryType?: CategoryTypeEnum) =>
          set((state) => ({
            ...state,
            categoryType,
          })),
        setCountryService: (countryService?: BookingCountryService) =>
          set((state) => ({
            ...state,
            countryService,
          })),
        setShop: (shop?: BookingShop) =>
          set((state) => ({
            ...state,
            shop,
          })),
        setTimeSlot: (timeSlot?: SlotInterface) =>
          set((state) => ({
            ...state,
            timeSlot,
          })),
        setInformations: (informations?: WidgetInformationsStepFormValues) =>
          set((state) => ({
            ...state,
            informations,
          })),
        setUser: (userInfo?: { sub: string; email: string }) =>
          set((state) => ({
            ...state,
            user: userInfo,
          })),
        reset: () =>
          set((state) => ({
            ...state,
            ...defaultInitState,
          })),
        setHasHydrated: (value: boolean) =>
          set((state) => ({
            ...state,
            _hasHydrated: value,
          })),
      }),
      {
        name: 'widget-steps-store',
        onRehydrateStorage: (state) => {
          return () => state.setHasHydrated(true);
        },
      },
    ),
  );
};
